/**
 * Small shared primitives.
 *
 * Every colour here is a theme variable rather than a literal, so the admin
 * theme editor restyles the whole app without touching component code.
 */

import { Link } from 'react-router-dom';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-ink hover:opacity-90 disabled:opacity-40 border border-primary',
  secondary:
    'bg-transparent text-ink border border-ink hover:bg-ink hover:text-background disabled:opacity-40',
  ghost: 'bg-transparent text-link hover:text-ink underline underline-offset-4 border-0',
  danger: 'bg-error text-white hover:opacity-90 border border-error disabled:opacity-40',
};

const SIZES: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3.5 text-base',
};

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 font-body tracking-wide uppercase ' +
  'transition-opacity duration-150 cursor-pointer disabled:cursor-not-allowed ' +
  'rounded-[var(--radius-button)]';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      // Announce busy state to assistive tech, not just visually.
      aria-busy={loading || undefined}
      className={`${BUTTON_BASE} ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: { to: string; variant?: Variant; size?: Size; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>) {
  return (
    <Link
      to={to}
      {...rest}
      className={`${BUTTON_BASE} ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    >
      {children}
    </Link>
  );
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Alert({
  tone = 'error',
  children,
  onDismiss,
}: {
  tone?: 'error' | 'success' | 'info';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const tones = {
    error: 'border-error text-error bg-error/5',
    success: 'border-success text-success bg-success/5',
    info: 'border-border text-ink bg-surface',
  };
  return (
    <div
      // Errors interrupt; informational messages wait for a pause.
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 border px-4 py-3 text-sm ${tones[tone]}`}
    >
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 cursor-pointer opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-20 text-center">
      <h2 className="text-2xl">{title}</h2>
      <p className="max-w-md text-sm text-muted">{message}</p>
      {action}
    </div>
  );
}

export function PageHeading({
  title,
  description,
  count,
}: {
  title: string;
  description?: string;
  count?: number;
}) {
  return (
    <header className="border-b border-border pb-6">
      <h1 className="text-3xl md:text-4xl">{title}</h1>
      {description && (
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">{description}</p>
      )}
      {count !== undefined && (
        <p className="mt-3 text-xs uppercase tracking-widest text-muted">
          {count} {count === 1 ? 'piece' : 'pieces'}
        </p>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-3 xl:grid-cols-4"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-square w-full bg-surface" />
          <div className="mt-4 h-3 w-3/4 bg-surface" />
          <div className="mt-2 h-3 w-1/3 bg-surface" />
        </div>
      ))}
    </div>
  );
}
